import * as React from 'react'

import { Theme } from '@mui/material/styles'

import { getEmailHistoryStatus } from '@user/store/agreements/utils/getEmailHistoryStatus'
import { useGetEmailHistoryOnMount } from '@user/store/agreements/hooks/useGetEmailHistoryOnMount'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useEffect } from 'react'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { timeFormat } from '@sales-center/common'

const styles = {
  cell: {
    borderBottom: 'none',
  },

  headerCell: {
    borderBottom: 'none',
    fontFamily: 'Source Sans Pro Semibold',
  },

  leftCell: {
    paddingLeft: (theme: Theme) => theme.spacing(0),
  },
}

interface Props {
  agreementId: number
  updatePosition?: () => void
}

export function EmailHistory(props: Props) {
  const { agreementId, updatePosition } = props

  const { status, history } = useGetEmailHistoryOnMount(agreementId)
  const { formatDate } = useLocalizationDateFormat()

  useEffect(
    () => updatePosition && void updatePosition(),
    [status, updatePosition],
  )

  return (
    <>
      {status == 'pending' && (
        <Box p={2} display="flex">
          <CircularProgress />
        </Box>
      )}
      {status == 'fail' && (
        <Box pb={1}>
          <Alert severity="error">Failed to load email history.</Alert>
        </Box>
      )}
      {status == 'success' && (
        <>
          {!history?.records.length && (
            <Typography>
              There is currently no email history to display.
            </Typography>
          )}

          {!!history?.records.length && (
            <Table size="small" style={{ width: 'auto' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...styles.headerCell, ...styles.leftCell }}>
                    Date Sent
                  </TableCell>
                  <TableCell sx={styles.headerCell}>Email Sent To</TableCell>
                  <TableCell sx={styles.headerCell}>Email Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.records.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ ...styles.cell, ...styles.leftCell }}>
                      {!!record.date_sent
                        ? formatDate(record.date_sent) +
                          ' at ' +
                          timeFormat(record.date_sent)
                        : ''}
                    </TableCell>
                    <TableCell sx={styles.cell}>
                      <a
                        href={`mailto:${record.emailAddress}`}
                        target="_blank"
                        rel="noreferrer"
                        data-test-id={`email-history-link-${record.emailAddress}`}
                      >
                        {record.emailAddress}
                      </a>
                    </TableCell>
                    <TableCell sx={styles.cell}>
                      <i>{getEmailHistoryStatus(record)}</i>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </>
  )
}
